/**
 * Public Trip Share Page
 * 
 * Displays a shared trip's live location (read-only, no auth required)
 * Privacy-focused: only shows position, speed (optional), and last update
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Icon } from 'leaflet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Navigation, Clock, Anchor, Ship, AlertTriangle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

// Boat icon for the marker
const boatIcon = new Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#0ea5e9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1 .6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/>
      <path d="M19.38 20A11.6 11.6 0 0 0 21 14l-9-4-9 4c0 2.9.94 5.34 2.81 7.76"/>
      <path d="M12 2v8"/>
      <path d="M12 6l4 2"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const SharePage = () => {
  const { token } = useParams();
  const [shareData, setShareData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  
  const fetchShareData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/share/${token}`);
      
      if (response.status === 404) {
        setError('This share link has expired, been revoked, or the trip has ended.');
        setLoading(false);
        return;
      }
      
      if (!response.ok) {
        setError('Failed to load shared trip data.');
        setLoading(false);
        return;
      }
      
      const data = await response.json();
      setShareData(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching share data:', err);
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [API_BASE, token]);
  
  // Initial fetch
  useEffect(() => {
    fetchShareData();
  }, [fetchShareData]);
  
  // Auto-refresh every 15 seconds
  useEffect(() => {
    if (!shareData) return;
    
    const interval = setInterval(fetchShareData, 15000);
    return () => clearInterval(interval);
  }, [shareData, fetchShareData]);
  
  // Get motion state icon and label
  const getMotionInfo = (state) => {
    switch (state) {
      case 'UNDERWAY':
        return { icon: Ship, label: 'Underway', color: 'bg-green-500' };
      case 'DRIFTING':
        return { icon: Navigation, label: 'Drifting', color: 'bg-yellow-500' };
      case 'IDLE':
      case 'ANCHORED':
        return { icon: Anchor, label: 'Anchored', color: 'bg-blue-500' };
      default:
        return { icon: Ship, label: 'Active', color: 'bg-cyan-500' };
    }
  };
  
  // Format last updated
  const getLastUpdated = () => {
    if (!shareData?.last_updated) return 'Unknown';
    try {
      return formatDistanceToNow(new Date(shareData.last_updated), { addSuffix: true });
    } catch {
      return 'Recently';
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Ship className="w-12 h-12 text-cyan-400 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-400">Loading trip...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-gray-800 border-gray-700">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-orange-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Trip Not Available</h2>
            <p className="text-gray-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const motionInfo = getMotionInfo(shareData?.motion_state);
  const MotionIcon = motionInfo.icon;
  
  return (
    <div className="min-h-screen bg-gray-900 flex flex-col" data-testid="share-page">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-3">
        <div className="flex items-center justify-between max-w-screen-xl mx-auto">
          <div className="flex items-center gap-3">
            <Ship className="w-6 h-6 text-cyan-400" />
            <div>
              <h1 className="text-lg font-bold text-white">
                {shareData?.display_name || 'Boater'}&apos;s Trip
              </h1>
              <p className="text-xs text-gray-400">Live location sharing</p>
            </div>
          </div>
          
          <Badge className={motionInfo.color}>
            <MotionIcon className="w-3 h-3 mr-1" />
            {motionInfo.label}
          </Badge>
        </div>
      </div>
      
      {/* Map */}
      <div className="flex-1 relative">
        {shareData && (
          <MapContainer
            center={[shareData.lat, shareData.lon]}
            zoom={14}
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
          >
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            
            <Marker position={[shareData.lat, shareData.lon]} icon={boatIcon}>
              <Popup>
                <div className="text-center">
                  <strong>{shareData.display_name}</strong>
                  {shareData.speed !== null && (
                    <p className="text-sm">{shareData.speed.toFixed(1)} kt</p>
                  )}
                </div>
              </Popup>
            </Marker>
          </MapContainer>
        )}
      </div>
      
      {/* Info Panel */}
      <div className="bg-gray-800 border-t border-gray-700 px-4 py-4">
        <div className="max-w-screen-xl mx-auto">
          <Card className="bg-gray-700 border-gray-600">
            <CardContent className="p-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Last Updated */}
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-400">Last Update</p>
                    <p className="text-sm text-white font-medium">{getLastUpdated()}</p>
                  </div>
                </div>
                
                {/* Speed (if included) */}
                {shareData?.speed !== null && (
                  <div className="flex items-center gap-2">
                    <Navigation className="w-4 h-4 text-cyan-400" />
                    <div>
                      <p className="text-xs text-gray-400">Speed</p>
                      <p className="text-sm text-white font-medium">
                        {shareData.speed.toFixed(1)} knots
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Heading (if included) */}
                {shareData?.heading !== null && shareData?.speed !== null && (
                  <div className="flex items-center gap-2">
                    <Navigation 
                      className="w-4 h-4 text-cyan-400" 
                      style={{ transform: `rotate(${shareData.heading}deg)` }}
                    />
                    <div>
                      <p className="text-xs text-gray-400">Heading</p>
                      <p className="text-sm text-white font-medium">
                        {Math.round(shareData.heading)}°
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Privacy notice */}
              <p className="text-xs text-gray-500 mt-4 text-center">
                This link was shared by the boater. It will expire when the trip ends.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SharePage;
